<template>
    <div>
        <div class="selectTree">
            <div class="left">
                <div class="left-head">
                    <el-input
                        v-model="state.orgWord"
                        class="input"
                        placeholder="搜索组织"
                        @input="filterTree"
                    />
                </div>
                <div class="tree">
                    <div class="treeCn">
                        <el-tree
                            v-if="!state.orgWord"
                            ref="treeRef"
                            node-key="orgId"
                            lazy
                            :load="loadNode"
                            :data="state.treeData"
                            :props="defaultProps"
                            :default-expanded-keys="state.expendTree"
                            @node-click="handleNodeClick"
                        />
                        <el-tree
                            v-if="state.orgWord"
                            ref="treeRef"
                            node-key="orgId"
                            default-expand-all
                            :data="state.treeData"
                            :props="defaultProps"
                            @node-click="handleNodeClick"
                        />
                    </div>
                </div>
            </div>
            <div class="center">
                <div class="center-head">
                  <el-input @keyup.enter="loadSearchNode" @input="loadSearchNode" v-model="state.userKeyword" clearable class="input" placeholder="搜索组织">
                    <template #prefix>
                      <el-icon class="el-input__icon"><search /></el-icon>
                    </template>
                  </el-input>
                </div>
                <div class="center-all">
                  <span v-show="state.currOrg && state.operateStatus === 1" class="center-all-cn">
                    <span>{{ state.currOrg }} （{{ state.checkBackList.length }}/{{ state.getBackList.length }}）</span>
                  </span>
                  <span v-show="state.operateStatus === 2" class="center-all-cn">搜索结果</span>
                </div>
                <div
                    v-loading="listLoading"
                    class="center-lists"
                >
                    <el-table ref="selectionTableRef" :data="state.getBackList" @select-all="handleCheckAllChange" @selection-change="checkBoxChange">
                      <el-table-column type="selection" width="50px"></el-table-column>
                      <el-table-column show-overflow-tooltip prop="orgName" label="组织名称" width="200px"></el-table-column>
                      <el-table-column show-overflow-tooltip prop="pathName" label="组织路径"></el-table-column>
                  </el-table>
                </div>
            </div>
            <div class="right">
                <div class="right-head">
                    已选
                    <span
                        class="clear"
                        @click="clearAlluser"
                    >
                        清空
                    </span>
                </div>
                <div class="right-lists">
                    <div
                        v-for="(item,index) in state.checkList"
                        :key="item.orgId"
                        class="rList"
                    >
                        <span class="rList-name">
                            <span class="name">{{ item.orgName }}</span>
                        </span>
                        <el-icon @click="delCheckOrg(index)"><CloseBold /></el-icon>
                    </div>
                </div>
            </div>
        </div>
        <div class="btns">
            <el-button
                type="primary"
                @click="confirm"
            >确定</el-button>
            <el-button @click="handleClose">取消</el-button>
        </div>
    </div>
</template>

<script setup>
import { inject, nextTick, onBeforeMount, reactive, ref, watch } from 'vue';
import axios from '@/plugins/axios';
import { ElMessageBox, ElMessage } from 'element-plus';
import { countStr, throttle } from '@/utils';

const handleClose = inject('handleClose');

const emits = defineEmits(['change']);

const treeRef = ref('');
const selectionTableRef = ref('');
const loading = ref(false);
const listLoading = ref(false);

const defaultProps = {
    value: 'orgId',
    label: 'orgName',
    children: 'childs',
    disabled: 'dis'
};

const props = defineProps({
    list: {
        type: Array,
        default: () => []
    },
    type: {
        type: String,
        default: () => 'multiple' // 默认多选
    },
    maxLength: {
        type: Number,
        default: () => 999999 // 多选的配置下最多可选择多少人
    },
    onlyCompany: {
        type: Boolean,
        default: () => false // 仅支持选择公司
    },
});

const state = reactive({
    treeData: [],
    orgWord: '',
    userKeyword: '',
    selectOptions: [],
    expendTree: [],
    currOrg: '', // 当前组织名称
    getBackList: [], // 接口返回所有的数据
    checkBackList: [], // 接口返回选中的数据
    lastCheckList: [], // 记录上一次选中的数据
    checkList: [], // 选中的列表
    operateStatus: 0 // 操作状态：0: 近期添加，1：点击组织树节点，2:搜索组织
});

watch(() => props.list, val => {
    state.checkList = JSON.parse(JSON.stringify(val));
}, { immediate: true });

onBeforeMount(() => {
    getAllOrg();
});

const dataFilter = (data = []) => {
  return props.onlyCompany? data.filter(item => Object.is(item['isCompany'], 1)) : data;
};

// 获取所有组织
const getAllOrg = async() => {
    const res = await axios({
        url: '/user-control/v1/top-org',
        method: 'get',
        headers: {
            'Content-Type': 'application/json'
        }
    });
    const tree = [];
    tree[0] = res.data[0];
    state.treeData = tree;
    state.originTree = JSON.parse(JSON.stringify(tree));
    state.expendTree.push(state.treeData[0].orgId);
};

const loadNode = async(node, resolve) => {
    if (node.data.id) {
        const res = await axios({
            url: '/user-control/v1/child-org',
            method: 'get',
            headers: {
                'Content-Type': 'application/json'
            },
            params: {
                pid: node.data.id,
                typeCode: 'Org'
            }
        });
        if (res.data.orgControlVos) {
            resolve(res.data.orgControlVos);
        } else {
            resolve([]);
        }
    }
};

// 点击按钮确定
const confirm = () => {
    if (props.type === 'single' && state.checkList.length > 1) {
      ElMessage.error(`只能选择一个组织！`);
      return;
    }
    if (props.type === 'multiple' && state.checkList.length > props.maxLength) {
      ElMessage.error(`最多只能选择${props.maxLength}个组织！`);
      return;
    }
    emits('change', state.checkList);
    handleClose();
};

const filterNode = (value, data) => {
    if (!value) return true;
    return data.name.includes(value);
};

const filterTree = throttle(async() => {
    if (!state.orgWord) {
        state.treeData = JSON.parse(JSON.stringify(state.originTree));
        return;
    }
    const res = await axios({
        method: 'get',
        url: '/user-control/v1/like-list',
        headers: {
            'Content-Type': 'application/json'
        },
        params: {
            keyword: state.orgWord,
            typeCode: 'Org',
            needPath: true
        }
    });
    state.treeData = res?.data?.orgControlVos || [];
}, 500);

// 全选款
const handleCheckAllChange = (val) => {
    state.checkBackList = val ? state.getBackList : [];

    if (state.checkBackList.length === 0) { // 全部删除
        const diff = state.getBackList.map(it => it.orgId);
        // 减少掉的要在checkList中删除
        state.checkList = state.checkList.filter(it => !diff.includes(it.orgId));
        state.lastCheckList = JSON.parse(JSON.stringify(state.checkList));
    } else { // 全部添加
        const diff = state.getBackList.map(it => it.orgId);
        const list = JSON.parse(JSON.stringify(state.checkList));
        const checkList = state.checkList.map(it => it.orgId); ;
        for (const i in diff) {
            if (!checkList.includes(diff[i])) {
                list.push(state.getBackList[i]);
            }
        }
        state.lastCheckList = list;
        state.checkList = JSON.parse(JSON.stringify(list));
    }
};

const checkBoxChange = (val) => {

    if (val.length > state.lastCheckList.length) { // 添加
        const hasCheckList = val.map(it => it.orgId);
        const lastCheckList = state.lastCheckList.map(it => it.orgId);
        const diff = [];
        // 计算出新增的哪几个列表
        for (const i in hasCheckList) {
            if (!lastCheckList.includes(hasCheckList[i])) {
                diff.push(hasCheckList[i]);
            }
        }
        const diffList = val.filter(it => diff.includes(it.orgId));
        // 新生成的加到checkList当中来
        const list = JSON.parse(JSON.stringify(state.checkList));
        const checkList = state.checkList.map(it => it.orgId); ;
        for (const i in diff) {
            if (!checkList.includes(diff[i])) {
                list.push(diffList[i]);
            }
        }
        state.checkList = list;
    } else if (val.length <= state.lastCheckList.length) { // 减少
        const hasCheckList = val.map(it => it.orgId);
        const lastCheckList = state.lastCheckList.map(it => it.orgId);
        const diff = [];
        // 计算出减少的哪几个列表
        for (const i in lastCheckList) {
            if (!hasCheckList.includes(lastCheckList[i])) {
                diff.push(lastCheckList[i]);
            }
        }
        // 减少掉的要在checkList中删除
        state.checkList = state.checkList.filter(it => !diff.includes(it.orgId));
    }
    state.lastCheckList = val;
    state.checkBackList = selectionTableRef.value.getSelectionRows();
};


// 更新待办列表
const setTableSelectionChecked = () => {
  // 如果已选中的列表存在，勾选要显示在checkBackList中
  const checkList = state.checkList.map(it => it.orgId);
  const getBackList = state.getBackList.map(it => it.orgId);
  for (const i in getBackList) {
      if (checkList.includes(getBackList[i])) {
          state.checkBackList.push(state.getBackList[i]);
          state.lastCheckList.push(state.getBackList[i]);
          nextTick(() => {
            selectionTableRef.value.toggleRowSelection(state.getBackList[i], true);
          });
      }
  }
}

const loadSearchNode = throttle(async() => {
    if (state.userKeyword) {
        loading.value = true;
        const res = await axios({
            method: 'get',
            url: '/user-control/v1/like-list',
            headers: {
                'Content-Type': 'application/json'
            },
            params: {
                keyword: state.userKeyword,
                typeCode: 'Org'
            }
        });
        state.operateStatus = 2;
        state.getBackList = dataFilter(JSON.parse(JSON.stringify(res?.data?.orgControlVos || [])));
        state.lastCheckList = [];
        state.checkBackList = [];
        loading.value = false;
        // 如果已选中的列表存在，勾选要显示在checkBackList中
        setTableSelectionChecked();
    }
}, 200);

// 显示是否选中
const showCheckOp = (item) => {
    let hasCheck = false;
    for (const i in state.checkList) {
        if (state.checkList[i].orgId === item.orgId) {
            hasCheck = true;
        }
    }
    return hasCheck;
};

const handleNodeClick = async(data) => {
    if (!data.id) {
        return;
    }
    listLoading.value = true;
    const res = await axios({
        url: '/user-control/v1/child-org',
        method: 'get',
        headers: {
            'Content-Type': 'application/json'
        },
        params: {
            pid: data.id,
            typeCode: 'Org'
        }
    });
    listLoading.value = false;
    state.operateStatus = 1;
    state.currOrg = data.orgName;
    const list = dataFilter(res?.data?.orgControlVos || []);
    state.getBackList = JSON.parse(JSON.stringify(list));
    state.lastCheckList = [];
    state.checkBackList = [];

    // 如果已选中的列表存在，勾选要显示在checkBackList中
    setTableSelectionChecked();
};

// 删除数据
const delCheckOrg = (index) => {
    const item = state.checkList[index];
    const rowItemIndex = state.getBackList.findIndex(it => Object.is(item.id, it.id)); // 找出在待选table中的索引
    state.checkList.splice(index, 1);
    state.checkBackList = state.checkBackList.filter(it => it.orgId !== item.orgId);
    state.lastCheckList = state.lastCheckList.filter(it => it.orgId !== item.orgId);
    selectionTableRef.value.toggleRowSelection(state.getBackList[rowItemIndex], false);
};

// 清空数据
const clearAlluser = () => {
    ElMessageBox.confirm(`确定要全部清空吗?`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
    }).then(async() => {
        state.checkList = [];
        state.lastCheckList = [];
        state.checkBackList = [];
        selectionTableRef.value.clearSelection();
    });
};

</script>

<style lang="scss" scoped>
.selectTree {
    display: flex;
    align-items: flex-start;
    height: 500px;
    padding-top: 7px;
    .left {
        width: 240px;
        border-right: 1px solid rgba(0, 0, 0, 0.15);
        height: 100%;
        .left-head {
            text-align: center;
            background-color: #E5E5E5;
            height: 48px;
            display: flex;
            align-items: center;
            justify-content: center;
            margin-bottom: 10px;
            .input {
                width: 208px;
            }
        }
        .tree {
            padding-left: 8px;
            box-sizing: border-box;
            max-height: 413px;
            max-width: 234px;
            overflow: auto;
            .treeCn {
                width: fit-content;
                min-width: 100%;
            }
        }
    }
    .center {
        width: 618px;
        border-right: 1px solid rgba(0, 0, 0, 0.15);
        height: 100%;
        .center-head {
            text-align: right;
            padding-right: 16px;
            box-sizing: border-box;
            background-color: #E5E5E5;
            height: 48px;
            display: flex;
            align-items: center;
            justify-content: flex-end;
            .input {
                width: 264px;
            }
        }
        .center-all {
            height: 40px;
            display: flex;
            align-items: center;
            padding-left: 16px;
            box-sizing: border-box;
            color:#262626;
            font-size: 14px;
            font-weight: bold;
            border-bottom: 1px solid rgba(0, 0, 0, 0.15);
            .center-all-cn {
                display: flex;
                align-items: center;
            }
        }
        .center-lists {
            color: #262626;
            font-size: 14px;
            height: 377px;
            overflow: auto;
            .item {
                display: flex;
                align-items: center;
                height: 40px;
                padding-left: 16px;
                box-sizing: border-box;
                &.active {
                    background-color: #DDF1FF;
                }
                .item-list {
                    display: flex;
                    align-items: center;
                    .name {
                        color: #262626;
                        margin-left: 8px;
                        width: 160px;
                        overflow: hidden;
                        white-space: nowrap;
                        text-overflow: ellipsis;
                        margin-right: 5px;
                    }
                    .job {
                        color: #262626;
                        max-width: 395px;
                        overflow: hidden;
                        white-space: nowrap;
                        text-overflow: ellipsis;
                    }
                }
            }
        }
    }
    .right {
        width: 241px;
        height: 100%;
        display: flex;
        flex-direction: column;
        .right-head {
            display: flex;
            align-items: center;
            justify-content: space-between;
            flex-shrink: 0;
            height: 48px;
            background-color: #E5E5E5;
            padding: 0 16px;
            box-sizing: border-box;
            color: #262626;
            font-weight: bold;
            .clear {
                color: #179AEE;
                cursor: pointer;
            }
        }
        .right-lists {
            flex: 1;
            overflow: auto;
            .rList {
                display: flex;
                align-items: center;
                height: 40px;
                justify-content: space-between;
                padding: 0 16px;
                box-sizing: border-box;
                color: #262626;
                font-size: 14px;
                cursor: pointer;
                .rList-name {
                    display: flex;
                    align-items: center;
                    img {
                        width: 24px;
                        height: 24px;
                        border-radius: 24px;
                        margin-right: 8px;
                    }
                }

            }
        }
    }
}
.btns {
    padding: 10px 0;
    display: flex;
    justify-content: flex-end;
    padding-right: 30px;
    border-top: 1px solid rgba(0, 0, 0, 0.15);
}
.option-item {
    display: flex;
    align-items: center;
    position: relative;
    .option-check {
        position: absolute;
        right: 0;
        top: 12px;
    }
    img {
        width: 24px;
        height: 24px;
        box-shadow: 0px 0px 11px 0px rgba(0,0,0,0.1);
        border-radius: 24px;
        margin-right: 8px;
    }
    .option-right {
        flex: 1;
        font-size: 13px;
        color: #666666;
        .op-name {
            font-weight: bold;
            color: #262626;
            height: 29px;
            .first {
                color: #179AEE;
            }
        }
        .op-position {
            max-width: 200px;
            word-break: break-all;    // 自动换行（兼容字母）
            overflow: hidden;       // 超出隐藏
            text-overflow: ellipsis;   // 溢出显示省略号
            display: -webkit-box;
            -webkit-line-clamp: 3;   // 显示3行
            -webkit-box-orient: vertical;
            white-space: break-spaces;
            line-height: 19px;
            font-weight: 400;
        }

    }
}
</style>
